<template>
    <div
        v-bind:style="draggableStyleObject"
        v-bind:class="orderableItemClass">
        <div
            class="d-flex align-items-end"
            v-bind:class="{ 'flex-column': horizontal }">
            <div
                v-if="showArrows"
                class="d-flex align-items-center justify-content-between align-self-stretch"
                v-bind:class="{ 'flex-column': !horizontal }">
                <!-- there are some issues in iOS with click events not being fired properly.
                this is probably due to being within the interact component. to get around this we
                explicitly listen to the touchstart event and prevent the default to avoid dup events -->
                <div
                    class="prev"
                    v-bind:class="{ 'rotate-icon': horizontal }"
                    v-on:touchstart.prevent="$emit('prev', itemKey)"
                    v-on:click="$emit('prev', itemKey)" />
                <div
                    class="next"
                    v-bind:class="{ 'rotate-icon': horizontal }"
                    v-on:touchstart.prevent="$emit('next', itemKey)"
                    v-on:click="$emit('next', itemKey)" />
            </div>
            <div class="flex-grow-1 w-100">
                <slot v-bind:item-key="itemKey">
                    {{ itemKey }}
                </slot>
            </div>
            <div
                v-if="showAddRemove"
                class="d-flex align-items-center justify-content-between align-self-stretch"
                v-bind:class="{ 'flex-column': !horizontal }">
                <div
                    class="add"
                    v-on:touchstart.prevent="$emit('add', itemKey)"
                    v-on:click="$emit('add', itemKey)" />
                <div
                    class="del"
                    v-on:touchstart.prevent="$emit('del', itemKey)"
                    v-on:click="$emit('del', itemKey)" />
            </div>
        </div>
    </div>
</template>

<script>
    import { eventIsInsideRect } from "./_util";

    export default {
        props: {
            itemKey: { type: String, required: true },
            horizontal: { type: Boolean, required: false, default: false },
            showAddRemove: { type: Boolean, required: false, default: false },
            showArrows: { type: Boolean, required: false, default: false },
            showItemShadowBox: { type: Boolean, required: false, default: true },
            readOnly: { type: Boolean, required: false, default: false }
        },
        emits: ["prev", "next", "add", "del"],
        data() {
            return {
                moving: false,
                x: 0,
                y: 0,
                top0: 0,
                left0: 0,
                dragEvent: null,
                rect: null,
                hover: false,
                ordering: false,
                overrideClassObject: {}
            };
        },
        computed: {
            draggableClassObject() {
                const defaultObj = {
                    "d-flex": true,
                    card: true,
                    "shadow-sm": this.hover
                };
                return { ...defaultObj, ...this.overrideClassObject };
            },
            h() {
                if (this.rect) {
                    return this.rect.height;
                }
                return 0;
            },
            w() {
                if (this.rect) {
                    return this.rect.width;
                }
                return 0;
            },
            orderableItemClass() {
                return {
                    "orderable-item": !this.readOnly,
                    "orderable-item-shadowbox": this.showItemShadowBox
                };
            },
            draggableStyleObject() {
                if (!this.moving) {
                    return null;
                }
                const res = {
                    top: `${this.y}px`,
                    left: `${this.x}px`,
                    opacity: ".8",
                    transitionProperty: "none",
                    transitionDuration: "0s",
                    position: "absolute",
                    height: `${this.h}px`,
                    width: `${this.w}px`,
                    "z-index": "10"
                };
                return res;
            }
        },
        methods: {
            setOverrideClass(classObject) {
                this.overrideClassObject = classObject;
            },
            mouseOver() {
                this.hover = true;
            },
            mouseOut() {
                this.hover = false;
            },
            updateClientRect() {
                this.rect = this.$el.getBoundingClientRect();
            },
            dragStart() {
                this.updateClientRect();
                const parentRect = this.$parent.$el.getBoundingClientRect();
                this.parentRect0 = parentRect;
                /**
                 * Calculate this items inital position relative to the parent.
                 * First calculate the offset of items client rect to parents.
                 * Then adjust for any item margin.
                 * The parent padding is always 0, otherwise we would need to calc that as well
                 * */
                const style = window.getComputedStyle(this.$el);
                const leftItemMargin = this.stringPxToFloat(style.marginLeft);
                const topItemMargin = this.stringPxToFloat(style.marginTop);
                this.left0 = this.rect.left - parentRect.left - leftItemMargin;
                this.top0 = this.rect.top - parentRect.top - topItemMargin;
                this.x = this.left0;
                this.y = this.top0;
                this.moving = true;
                window.requestAnimationFrame(this.calcDragMove);
            },
            stringPxToFloat(px) {
                const res = parseFloat(px);
                if (isNaN(res)) {
                    return 0;
                }
                return res;
            },
            calcDragMove() {
                if (!this.dragEvent.processed) {
                    this.dragEvent.processed = true;
                    const parentRect = this.$parent.$el.getBoundingClientRect();
                    const event = this.dragEvent;
                    /**
                     * calculate x and y positions relative to the parent
                     * Take the initial position(left/top 0) and add the dragEvent total delta (client - client0)
                     * Then cancel out any parent movements due to scrolling (parentRect - parentRect0)
                     */
                    this.x = this.left0 + (event.clientX - event.clientX0) - (parentRect.left - this.parentRect0.left);
                    this.y = this.top0 + (event.clientY - event.clientY0) - (parentRect.top - this.parentRect0.top);
                }
                if (this.moving) {
                    window.requestAnimationFrame(this.calcDragMove);
                }
            },
            dragMove(event) {
                this.dragEvent = event;
            },
            dragEnd() {
                this.x = 0;
                this.y = 0;
                this.moving = false;
            },
            isInside(dragEvent) {
                this.updateClientRect();
                return eventIsInsideRect(this.rect, dragEvent);
            },
            setOrdering() {
                const clear = () => {
                    this.ordering = false;
                };
                if (this.timeout) {
                    clearTimeout(this.timeout);
                }
                this.timeout = setTimeout(clear, 400);
            },
            getInsertPosition(dragEvent) {
                const coord = this.horizontal ? dragEvent.clientX : dragEvent.clientY;
                const threshold = this.horizontal
                    ? this.rect.left + this.rect.width / 2
                    : this.rect.top + this.rect.height / 2;
                if (coord < threshold) {
                    return "before";
                } else {
                    return "after";
                }
            }
        }
    };
</script>
<style lang="scss" scoped>
    $orderable-icon-img-size: 1rem;
    $orderable-icon-size: 2rem;
    %icon {
        background-size: $orderable-icon-img-size $orderable-icon-img-size;
        height: $orderable-icon-size;
        width: $orderable-icon-size;
        background-repeat: no-repeat;
        background-position: center;
        cursor: pointer;
    }
    %icon:hover {
        box-shadow: var(--bs-box-shadow-sm);
    }
    .empty-icon {
        @extend %icon;
        pointer-events: none;
        &:hover {
            box-shadow: none;
        }
    }
    .prev {
        @extend %icon;
        background-image: url("Images/icons/uparrow.svg");
    }
    .next {
        @extend %icon;
        background-image: url("Images/icons/downarrow.svg");
    }
    .del {
        @extend %icon;
        /*
            need to use the before  pseudo elemnt since we need to use filter to invert the icon
            and we dont want to interfere with the box-shadow
        */
        &::before {
            @extend %icon;
            content: "";
            display: block;
            background-image: url("Images/icons/trash.svg");
            filter: invert(100%);
        }
    }
    .add {
        @extend %icon;
        /*
            need to use the before  pseudo elemnt since we need to use filter to invert the icon
            and we dont want to interfere with the box-shadow
        */
        &::before {
            @extend %icon;
            content: "";
            display: block;
            background-image: url("Images/icons/add.svg");
            filter: invert(100%);
            background-size: $orderable-icon-img-size * 0.8 $orderable-icon-img-size * 0.8;
        }
    }
    // IE doesnt support the filter css rule. so instead we detect this with a vendor specific media query and add a
    // non white background to the del icon. not ideal but it works
    @media all and (-ms-high-contrast: none), (-ms-high-contrast: active) {
        .del::before {
            background-color: var(--bs-primary);
        }
    }
    .rotate-icon {
        transform: rotate(-90deg);
    }
    .orderable-item:hover {
        cursor: move;
        cursor: grab;
    }
    .orderable-item:active {
        cursor: grabbing;
    }
    .orderable-item-shadowbox {
        box-shadow: var(--bs-box-shadow-sm);
    }
</style>
